import * as React from "react"
import Slider, { Settings } from "react-slick"
import styled from "styled-components"
import ArticleCard from "../news/ArticleCard"
import ResourceCard from "../../atoms/ResourceCard"
import ReadMoreNewsCard from "../news/ReadMoreNewsCard"
import { Col, Row, useScreenClass } from "react-grid-system"
import { ArrowButtonLeft, ArrowButtonRight } from "../../atoms/icons/ArrowButtonIcons"
import { Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import PageDot from "./PageDot"
import { ArticleModel } from "../../contentful/models"

const NewCarouselStyle = styled.div`
  .slick-track {
    display: flex;

    &::before {
      content: none;
    }
  }

  .slick-dots li {
    margin-left: -3px;
    margin-right: -3px;
  }

  .slick-list {
    margin-inline: -1rem;
  }

  .slick-slide {
    height: inherit;
    margin: 5px;

    & > div {
      height: 100%;
    }

    & {
      margin-inline: 1rem;
      outline: red solid 2px;
    }
  }

  ${ResourceCard} {
    height: 100%;
  }
`

interface Props {
  news: ArticleModel<string>[]
}

interface CarouselControlsProps {
  dots: React.ReactNode
  firstSlide?: boolean
  lastSlide?: boolean
  onClickPrevious?: React.MouseEventHandler
  onClickNext?: React.MouseEventHandler
}

const CarouselControls: React.FunctionComponent<CarouselControlsProps> = ({
  dots,
  onClickNext,
  onClickPrevious,
  firstSlide,
  lastSlide,
}) => {
  return (
    <Row justify="center" align="center">
      <div onClick={(e) => !firstSlide && onClickPrevious?.(e)} style={{ cursor: firstSlide ? "auto" : "pointer" }}>
        <ArrowButtonLeft color={firstSlide ? theme.colors.greyDisabled : theme.colors.dark} />
      </div>
      <Spacer spacing={1} />
      {dots}
      <Spacer spacing={1} />
      <div onClick={(e) => !lastSlide && onClickNext?.(e)} style={{ cursor: lastSlide ? "auto" : "pointer" }}>
        <ArrowButtonRight color={lastSlide ? theme.colors.greyDisabled : theme.colors.dark} />
      </div>
    </Row>
  )
}

const NewsCarousel: React.FunctionComponent<Props> = ({ news }) => {
  const initialSlide = 0

  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(initialSlide)
  const sliderRef = React.useRef<Slider>(null)

  const screen = useScreenClass()
  const slidesToShow = ["xs", "sm", "md"].includes(screen) ? 1 : 2

  const newsCards = news.map((article) => {
    return <ArticleCard key={article.sys?.id} {...article} />
  })

  const slides = [...newsCards, <ReadMoreNewsCard key="read-more-news" />]
  const numSlides = slides.length - 1
  const lastSlide = currentSlideIndex == numSlides - (slidesToShow - 1)
  const firstSlide = currentSlideIndex == 0

  const settings: Settings = {
    afterChange: setCurrentSlideIndex,
    beforeChange: setCurrentSlideIndex,
    slidesToScroll: slidesToShow,
    slidesToShow,
    initialSlide,
    arrows: false,
    dots: true,
    infinite: false,
    appendDots: (dots) => (
      <Col>
        <CarouselControls
          dots={dots}
          firstSlide={firstSlide}
          lastSlide={lastSlide}
          // Simple using: onClickPrevious={sliderRef.current?.slickPrev} here
          // results in flakiness (where buttons can't be clicked) since this binds to ref.current?.clickPrev,
          // but this can be nil while rendering. This problem is fixed by a closure which binds to the ref, which should not
          // have a current field that is not undefined at the time when the user clicks on the
          // button.
          onClickPrevious={() => {
            sliderRef.current?.slickPrev()
          }}
          onClickNext={() => {
            sliderRef.current?.slickNext()
          }}
        />
      </Col>
    ),
    customPaging: () => <PageDot />,
  }

  return (
    <NewCarouselStyle>
      <Slider className="slider" ref={sliderRef} {...settings}>
        {slides}
      </Slider>

      <link
        rel="stylesheet"
        type="text/css"
        charSet="UTF-8"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
      />
    </NewCarouselStyle>
  )
}

export default NewsCarousel
